<template>
  <section v-if="visible" ref="fsRoot" class="maquina-estado-v1-root">
    <div class="toolbar-row">
      <div class="toolbar-left">
        <div class="toolbar-title">
          <v-icon size="20" color="#0f5f97">mdi-monitor-dashboard</v-icon>
          Estado de Maquinas NYX
        </div>
        <div v-if="!loading" class="machine-count-pill">{{ maquinas.length }} maquinas</div>
        <div v-if="!loading" class="page-pill">Siguiente actualización: {{ countdownTxt }}</div>
      </div>
      <button class="fs-btn" @click="extenderPantalla">
        <v-icon size="16" class="mr-1">{{ fsIcon }}</v-icon>
        <span class="fs-btn-label">{{ fsLabel }}</span>
      </button>
    </div>

    <v-card elevation="0" class="dashboard-card">
      <v-card-text class="dashboard-body">
        <v-autocomplete
          v-model="idArea"
          :items="areas"
          label="Area"
          item-value="idArea"
          item-text="nombre"
          hide-details
          clearable
          outlined
          dense
          class="area-select mb-4"
        />

        <div class="filter-chips mb-5">
          <button
            v-for="(filtro, idx) in filtros"
            :key="'filtro-' + idx"
            class="filter-chip"
            :class="{ active: filtroValor === idx }"
            :style="filtroValor === idx ? activeChipStyle(filtro.opcion) : {}"
            @click="filtroValor = idx"
          >
            <span v-if="filtro.opcion !== null" class="chip-dot" :style="{ background: colorCard(filtro.opcion) }"></span>
            {{ filtro.nombre }}
            <span class="chip-count">{{ contarFiltro(filtro.opcion) }}</span>
          </button>
        </div>

        <div v-if="loading && maquinas.length === 0" class="loading-state">
          <v-progress-circular :size="64" :width="5" color="primary" indeterminate />
          <p class="loading-text">Cargando maquinas...</p>
        </div>

        <v-row v-if="visible && maquinas.length > 0" justify="start" align="stretch">
          <v-col
            v-for="maquina in maquinasEnPantalla"
            :key="'maq-' + maquina.idMaquinaIns"
            :cols="12"
            :sm="dynamicCols.sm"
            :md="dynamicCols.md"
            :lg="dynamicCols.lg"
            :xl="dynamicCols.xl"
            class="d-flex"
            style="padding: 10px"
          >
            <CardMaquinaNyx :item="maquina" style="width: 100%" />
          </v-col>

          <!-- Tarjeta de pendientes: cuantas maquinas CON datos faltan por cargar (solo carga inicial) -->
          <v-col
            v-if="progresiva && pendientesCargar > 0"
            key="maq-pendientes"
            :cols="12"
            :sm="dynamicCols.sm"
            :md="dynamicCols.md"
            :lg="dynamicCols.lg"
            :xl="dynamicCols.xl"
            class="d-flex"
            style="padding: 10px"
          >
            <div class="pending-card" style="width: 100%">
              <v-progress-circular :size="30" :width="3" color="#0f5f97" indeterminate />
              <div class="pending-num">{{ pendientesCargar }}</div>
              <div class="pending-lbl">{{ pendientesCargar === 1 ? 'maquina pendiente' : 'maquinas pendientes' }}</div>
            </div>
          </v-col>
        </v-row>

        <div v-if="!loading && !maquinasFiltradas.length" class="empty-state">
          <v-icon size="32" color="#8a95a3">mdi-information-outline</v-icon>
          <p>No hay maquinas para mostrar con ese filtro.</p>
        </div>
      </v-card-text>
    </v-card>
  </section>
</template>

<script>
import axios from "axios";
import moment from "moment";
import CardMaquinaNyx from "./MaquinaEstado/CardMaquinaNyx.vue";

const STATUS_COLORS = { 1: "#00C472", 2: "#2B7EFF", 3: "#FF9F00", 4: "#E53535" };
const CATEGORY_ROTATE_MS = 15000;
const DASH_REFRESH_MS = 300000;

export default {
  name: "EstadoMaquinaNyx",
  components: { CardMaquinaNyx },
  inheritAttrs: false,
  props: {
    value: { type: Boolean, default: undefined },
    active: { type: Boolean, default: undefined },
    pollMs: { type: Number, default: DASH_REFRESH_MS },
  },
  data: () => ({
    isFullscreen: false,
    loading: false,
    maquinas: [],
    instancias: [],
    turnos: [],
    areas: [],
    idArea: -1,
    filtroValor: 0,
    interval: null,
    categoryRotateInterval: null,
    totalCargar: 0,
    progresiva: false,
    nextUpdateSeconds: 0,
    intervalCountdown: null,
    loadGen: 0,
    tokensCache: {},
    sinDisponibilidad: false,
    desConParosNP: true,
    filtros: [
      { opcion: null, nombre: "Todas" },
      { opcion: 1, nombre: "Activas" },
      { opcion: 2, nombre: "Sin ODT" },
      { opcion: 3, nombre: "Paros Programados" },
      { opcion: 4, nombre: "Paros No Prog." },
    ],
  }),
  computed: {
    visible() {
      if (typeof this.active === "boolean") return this.active;
      if (typeof this.value === "boolean") return this.value;
      return true;
    },
    fsLabel() { return this.isFullscreen ? "Salir de pantalla" : "Pantalla completa"; },
    fsIcon() { return this.isFullscreen ? "mdi-arrow-collapse-all" : "mdi-arrow-expand-all"; },
    maquinasFiltradas() {
      const statusOrder = { 1: 0, 3: 1, 4: 2, 2: 3 };
      const opcion = this.filtros[this.filtroValor]?.opcion;
      const filtered = this.maquinas.filter((m) => (opcion == null ? true : m.status === opcion));
      if (opcion != null) return filtered;
      return filtered.slice().sort((a, b) => {
        const aOrder = statusOrder[a.status] ?? 99;
        const bOrder = statusOrder[b.status] ?? 99;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.codigoMaquina || "").localeCompare(String(b.codigoMaquina || ""));
      });
    },
    // Indices de los chips (excluyendo "Todas") que tienen al menos una maquina: sobre estos rota.
    categoriasConDatos() {
      const idx = [];
      this.filtros.forEach((f, i) => {
        if (f.opcion != null && this.contarFiltro(f.opcion) > 0) idx.push(i);
      });
      return idx;
    },
    // Lo que se pinta: mientras carga, en orden de carga (aparecen conforme llegan);
    // ya cargado, la categoria actual (con rotacion).
    maquinasEnPantalla() {
      return this.progresiva ? this.maquinas : this.maquinasFiltradas;
    },
    pendientesCargar() {
      return Math.max(0, (this.totalCargar || 0) - this.maquinas.length);
    },
    maquinasActivas() { return this.maquinas.filter((m) => m.status === 1); },
    // Columnas por fila iguales a Cazels: 3 por fila (lg/xl=4) fuera de pantalla completa.
    dynamicCols() {
      const total = this.maquinasEnPantalla.length;
      if (this.isFullscreen) {
        if (total <= 2) return { sm: 12, md: 6, lg: 6, xl: 6 };
        if (total <= 4) return { sm: 12, md: 6, lg: 4, xl: 3 };
        return { sm: 12, md: 4, lg: 3, xl: 2 };
      }
      return { sm: 12, md: 6, lg: 4, xl: 4 };
    },
    countdownTxt() {
      const s = Math.max(0, this.nextUpdateSeconds);
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    },
  },
  watch: {
    visible(n) { n ? this.iniciar() : this.detener(); },
    idArea() { this.refreshDashboard(); },
    filtroValor(nuevo) {
      // No tocar la rotacion mientras carga (se muestra en orden de carga).
      if (this.progresiva || this.loading) return;
      // "Todas" (opcion null) => detener rotacion y mostrar todo.
      if (nuevo === 0) { this.detenerRotacionCategorias(); return; }
      // Categoria concreta (click manual o rotacion): reiniciar el temporizador de 15s desde aqui.
      this.reiniciarTimerRotacion();
    },
  },
  mounted() {
    if (this.visible) this.iniciar();
    const sync = () => {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    document.addEventListener("MSFullscreenChange", sync);
    this._syncFs = sync;
  },
  beforeDestroy() {
    this.detener();
    if (this._syncFs) {
      document.removeEventListener("fullscreenchange", this._syncFs);
      document.removeEventListener("webkitfullscreenchange", this._syncFs);
      document.removeEventListener("MSFullscreenChange", this._syncFs);
    }
  },
  methods: {
    colorCard(status) { return STATUS_COLORS[status] || STATUS_COLORS[2]; },
    activeChipStyle(opcion) {
      const c = this.colorCard(opcion ?? 0);
      return { background: c + "15", color: c, borderColor: c + "50" };
    },
    contarFiltro(opcion) {
      if (opcion == null) return this.maquinas.length;
      return this.maquinas.filter((m) => m.status === opcion).length;
    },
    async extenderPantalla() {
      const el = this.$refs.fsRoot;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        await document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },
    // Rota el foco entre las categorias que tienen maquinas (Activas / Paro / Inactivas), 15s c/u.
    iniciarRotacionCategorias() {
      const cats = this.categoriasConDatos;
      if (!cats.length) { this.filtroValor = 0; this.detenerRotacionCategorias(); return; }
      // Posicionarse en la primera categoria con datos (si no estamos ya en una valida).
      if (!cats.includes(this.filtroValor)) this.filtroValor = cats[0];
      this.reiniciarTimerRotacion();
    },
    reiniciarTimerRotacion() {
      this.detenerRotacionCategorias();
      if (this.categoriasConDatos.length <= 1) return; // nada que rotar
      this.categoryRotateInterval = setInterval(() => {
        const cats = this.categoriasConDatos;
        if (!cats.length) return;
        const pos = cats.indexOf(this.filtroValor);
        this.filtroValor = cats[(pos + 1) % cats.length];
      }, CATEGORY_ROTATE_MS);
    },
    detenerRotacionCategorias() {
      if (this.categoryRotateInterval) clearInterval(this.categoryRotateInterval);
      this.categoryRotateInterval = null;
    },
    hoyMX() {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    },
    minutosHoyMX() {
      const [hh, mm] = new Intl.DateTimeFormat("en-GB", {
        timeZone: "America/Mexico_City",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date()).split(":").map(Number);
      return hh * 60 + mm;
    },
    timeToMin(hms = "00:00:00") {
      const [hh = "00", mm = "00"] = String(hms).split(":");
      return Number(hh) * 60 + Number(mm);
    },
    rangoMinutos(m, start, end) {
      return start <= end ? m >= start && m < end : m >= start || m < end;
    },
    turnoActualId() {
      const m = this.minutosHoyMX();
      if (Array.isArray(this.turnos) && this.turnos.length) {
        for (const t of this.turnos) {
          if (this.rangoMinutos(m, this.timeToMin(t.inicio), this.timeToMin(t.fin))) return Number(t.idTurno);
        }
        const ordered = this.turnos
          .map((t) => ({ id: Number(t.idTurno), start: this.timeToMin(t.inicio) }))
          .sort((a, b) => a.start - b.start);
        const last = ordered.filter((x) => x.start <= m).pop() || ordered[ordered.length - 1];
        if (last) return last.id;
      }
      return this.rangoMinutos(m, 19 * 60, 7 * 60) ? 1 : 2;
    },
    buildParams(idMaquinaIns) {
      return {
        idMaquinaIns,
        fechaRev: this.hoyMX(),
        idTurno: this.turnoActualId(),
        sinDisponibilidad: this.sinDisponibilidad,
        desConParosNP: this.desConParosNP,
      };
    },
    toPct(v) {
      const n = Number(v);
      return Number.isFinite(n) ? (n <= 1 ? n * 100 : n) : null;
    },
    getAreas() {
      return axios.get("/Areas")
        .then((r) => { this.areas = (r.data || []).filter((a) => a.idArea > 1); })
        .catch((e) => console.log(e));
    },
    async getInstancias() {
      try {
        // Catalogo ligero (id, nombre, idArea) en vez del GET pesado /MaquinaInstancias (~40 MB).
        const res = await axios.get("/MaquinaInstancias/EstadoCatalogo");
        this.instancias = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.log(e);
        this.instancias = [];
      }
    },
    async getTurnos() {
      try {
        const { data } = await axios.get("/Turnos");
        this.turnos = Array.isArray(data) ? data : [];
      } catch (e) {
        console.log(e);
        this.turnos = [];
      }
    },
    buildInactiveMachine(instancia, raw) {
      const resumen = raw?.resumen || {};
      return {
        idMaquinaIns: instancia?.idMaquinaIns ?? raw?.idMaquinaIns ?? null,
        codigoMaquina: resumen.nombreMaquina || resumen.codigoMaquina || instancia?.nombre || instancia?.codigo || "Maquina",
        status: 2,
        oee: null,
        disponibilidad: null,
        rendimiento: null,
        calidad: null,
        pizasOk: 0,
        planeacionPorTurno: 0,
        scrapPct: null,
        piezasScrap: 0,
        tcRealSegPza: 0,
        tcRealTxt: "—",
        tcQadSegPza: 0,
        tcQadTxt: "—",
        tiempoParoSegundos: 0,
        inicioParo: null,
        paroTipo: null,
        paroSubTipo: null,
        fechaInicio: null,
        _segundosInicialesParo: 0,
      };
    },
    // Extrae contexto (articulos, molde, paro tipo/inicio) de la MISMA respuesta de HoraPorHora,
    // sin llamadas extra: molde en resumen.codigoMolde, articulos en operacionesODT[].nombreArticulo,
    // y el paro mas reciente en horas[].parosNoProgramados / parosProgramados.
    datosDesdeHpH(ep) {
      const arr = (v) => (Array.isArray(v) ? v : []);
      const r = ep?.resumen || {};
      const ops = arr(ep?.operacionesODT);
      const horas = arr(ep?.horas);
      const articulosNombres = [...new Set(
        ops.map((o) => o?.nombreArticulo).filter(Boolean)
      )];
      // Paro mas reciente (mayor Inicio) entre todas las horas -> el que sigue en curso.
      let paro = null;
      for (const h of horas) {
        for (const p of [...arr(h?.parosNoProgramados), ...arr(h?.parosProgramados)]) {
          if (!paro || new Date(p.inicio).getTime() > new Date(paro.inicio).getTime()) paro = p;
        }
      }
      const inicioParoRaw = paro?.inicio ?? null;
      return {
        articulosNombres,
        codigoMolde: r.codigoMolde || null,
        paroTipo: paro?.causa || null,
        paroSubTipo: paro?.subCausa || null,
        inicioParoRaw,
        inicioParoTxt: inicioParoRaw ? moment(inicioParoRaw).format("HH:mm:ss") : null,
        tiempoParoEstado: 0,
      };
    },
    adaptEPResumen(ep, instancia) {
      const r = ep?.resumen || {};
      const ordenes = Array.isArray(ep?.ordenes) ? ep.ordenes : [];
      const cavRes = r?.resumenCavidades || {};
      const cavs = Array.isArray(cavRes.cavidades) ? cavRes.cavidades : [];
      const piezasOk = Number(cavRes?.piezasOkTotales ?? cavs.reduce((a, c) => a + (Number(c?.piezasOk) || 0), 0)) || 0;
      const piezasScrap = Number(cavRes?.piezasNoOkTotales ?? cavs.reduce((a, c) => a + (Number(c?.piezasNoOk) || 0), 0)) || 0;
      const booly = (v) => v === true || v === 1 || v === "1";
      const enParo = booly(r.hayParoEnCurso ?? r.HayParoEnCurso);
      const paroProg = booly(r.hayParoProgramadoEnCurso ?? r.HayParoProgramadoEnCurso);
      const paroNP = booly(r.hayParoNoProgramadoEnCurso ?? r.HayParoNoProgramadoEnCurso);
      const tcQadSegPza = ordenes.length > 0 ? Number(ordenes[0].tiempoCicloSeg ?? 0) : 0;
      const planeacionPorTurno = Number(r.piezasTeoricasTotales ?? 0);
      const totalPiezas = piezasOk + piezasScrap;
      const scrapPct = totalPiezas > 0 ? (piezasScrap / totalPiezas) * 100 : null;
      const hasData = enParo || ordenes.length > 0 || piezasOk > 0 || piezasScrap > 0 || planeacionPorTurno > 0 || tcQadSegPza > 0;
      if (!hasData) return this.buildInactiveMachine(instancia, ep);
      const ctx = this.datosDesdeHpH(ep);
      // Preferir el tiempo de paro que reporta el estado en tiempo real (mas exacto/vivo).
      const tiempoParoHpH = paroProg ? Number(r.segundosTiempoParoProgramado ?? 0) : Number(r.segundosTiempoParo ?? 0);
      const tiempoParoSegundos = ctx.tiempoParoEstado > 0 ? ctx.tiempoParoEstado : tiempoParoHpH;
      return {
        idMaquinaIns: ep.idMaquinaIns ?? instancia?.idMaquinaIns ?? null,
        codigoMaquina: r.nombreMaquina || r.codigoMaquina || ep.nombreMaquina || instancia?.nombre || instancia?.codigo || "",
        status: enParo ? (paroNP ? 4 : 3) : 1,
        oee: this.toPct(r.oee),
        disponibilidad: this.toPct(r.disponibilidad),
        rendimiento: this.toPct(r.rendimiento),
        calidad: this.toPct(r.calidad),
        pizasOk: piezasOk,
        planeacionPorTurno,
        scrapPct,
        piezasScrap,
        tcRealSegPza: 0,
        tcRealTxt: "—",
        tcQadSegPza,
        tcQadTxt: tcQadSegPza > 0 ? `${tcQadSegPza.toFixed(1)} seg` : "—",
        tiempoParoSegundos,
        _segundosInicialesParo: tiempoParoSegundos,
        // Enriquecimiento desde el estado en tiempo real (articulos / molde / paro).
        articulosNombres: ctx.articulosNombres,
        codigoMolde: ctx.codigoMolde,
        paroTipo: ctx.paroTipo,
        paroSubTipo: ctx.paroSubTipo,
        inicioParo: ctx.inicioParoTxt,
        fechaInicio: ctx.inicioParoRaw,
      };
    },
    async obtenerDatos() {
      if (!this.visible) return;
      // Cada corrida tiene su "generacion": si se dispara otra (cambio de area, refresh),
      // la anterior se cancela sola y no pisa los resultados nuevos.
      const gen = ++this.loadGen;
      this.loading = true;
      try {
        const instanciasFiltradas = (this.instancias || []).filter((x) => this.idArea > 1 ? x.idArea === this.idArea : true);
        const ids = instanciasFiltradas.map((x) => x.idMaquinaIns).filter(Boolean);
        const byId = Object.create(null);
        instanciasFiltradas.forEach((inst) => { byId[inst.idMaquinaIns] = inst; });
        if (!ids.length) { this.maquinas = []; this.totalCargar = 0; return; }

        // ¿Mismo conjunto que ya tenemos? => refresco de fondo (actualiza en sitio, sin parpadeo).
        // Si cambio (primera carga / cambio de area) => carga PROGRESIVA: empezamos vacio y cada
        // tarjeta APARECE cuando termina su propia peticion; al final se muestra cuantas faltan.
        const mismoSet = this.maquinas.length === ids.length
          && this.maquinas.every((m, i) => m.idMaquinaIns === ids[i]);

        this.totalCargar = ids.length;

        if (!mismoSet) {
          this.progresiva = true;
          this.detenerRotacionCategorias();
          this.filtroValor = 0;   // "Todas" mientras carga
          this.maquinas = [];
        }

        // Carga secuencial: una maquina a la vez; cada tarjeta aparece / se actualiza al terminar.
        for (let i = 0; i < ids.length; i++) {
          if (!this.visible || gen !== this.loadGen) return; // cancelada/superada
          const id = ids[i];
          const instancia = byId[id];
          let card;
          try {
            // timeout de 59s: si una peticion se cuelga (servidor ocupado), se abandona (queda sin datos)
            // y el widget sigue con las demas, sin quedarse esperando >1 min.
            const { data: raw } = await axios.get("/OEECalculos/HoraPorHora", { params: this.buildParams(id), timeout: 59000, silentNetError: true });
            if (gen !== this.loadGen) return;
            if (raw && raw.resumen) {
              raw.idMaquinaIns = id;
              card = this.adaptEPResumen(raw, instancia);
            } else {
              card = this.buildInactiveMachine(instancia, { idMaquinaIns: id });
            }
          } catch (e) {
            card = this.buildInactiveMachine(instancia, { idMaquinaIns: id });
          }
          if (gen !== this.loadGen) return;
          if (this.progresiva) this.maquinas.push(card);   // aparece apenas termina de cargar
          else this.$set(this.maquinas, i, card);          // refresco en sitio
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (gen === this.loadGen) {
          this.loading = false;
          if (this.progresiva) {
            this.progresiva = false;
            // Ya sabemos que categorias tienen datos: arrancar la rotacion 15s entre ellas.
            this.iniciarRotacionCategorias();
          }
        }
      }
    },
    resetCountdown() {
      this.nextUpdateSeconds = Math.max(1, Math.round((this.pollMs || DASH_REFRESH_MS) / 1000));
    },
    iniciarCountdown() {
      if (this.intervalCountdown) return;
      this.resetCountdown();
      this.intervalCountdown = setInterval(() => {
        this.nextUpdateSeconds = this.nextUpdateSeconds > 0 ? this.nextUpdateSeconds - 1 : 0;
      }, 1000);
    },
    detenerCountdown() {
      if (this.intervalCountdown) clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
    },
    async obtenerTokensMaquina(idMaquinaIns) {
      if (this.tokensCache[idMaquinaIns]) return this.tokensCache[idMaquinaIns];
      try {
        const { data } = await axios.get("/SensorDatos/TokensPorMaquina", { params: { idMaquinaIns }, silentNetError: true });
        const tokens = Array.isArray(data) ? data : [];
        if (tokens.length) this.tokensCache[idMaquinaIns] = tokens;
        return tokens;
      } catch (e) {
        console.error(`Error tokens maquina ${idMaquinaIns}:`, e);
        return [];
      }
    },
    async obtenerTcRealtimeMaquina(maquina) {
      if (!this.visible) return;
      if (!maquina || maquina.status !== 1) return;
      const tokens = await this.obtenerTokensMaquina(maquina.idMaquinaIns);
      if (!tokens.length) return;
      try {
        const params = {
          inicio: moment().subtract(5, "minutes").format("YYYY-MM-DD HH:mm:ss"),
          fin: moment().format("YYYY-MM-DD HH:mm:ss"),
          idMaquinaIns: maquina.idMaquinaIns,
          key: tokens[0],
        };
        const resp = await axios.get("/SensorDatos/SeriesRealtime", { params, silentNetError: true });
        const series = resp.data?.series || [];
        if (series.length && Array.isArray(series[0].data) && series[0].data.length) {
          const tc = Number(series[0].data[series[0].data.length - 1].y);
          if (Number.isFinite(tc) && tc > 0) {
            maquina.tcRealSegPza = tc;
            maquina.tcRealTxt = `${tc.toFixed(1)} seg`;
          }
        }
      } catch (e) {
        console.error(`Error TC realtime maquina ${maquina.idMaquinaIns}:`, e);
      }
    },
    async obtenerTcRealtimeActivasVisibles() {
      if (!this.maquinasActivas.length) return;
      await Promise.allSettled(this.maquinasActivas.map((m) => this.obtenerTcRealtimeMaquina(m)));
    },
    async refreshDashboard() {
      if (!this.visible) return;
      await this.obtenerDatos();
      await this.obtenerTcRealtimeActivasVisibles();
      this.resetCountdown();
    },
    async iniciar() {
      await this.getInstancias();
      await this.getTurnos();
      await this.getAreas();
      await this.refreshDashboard();
      if (!this.interval) this.interval = setInterval(() => this.refreshDashboard(), DASH_REFRESH_MS);
      this.iniciarCountdown();
      // La rotacion entre categorias se arranca al terminar la carga (dentro de obtenerDatos).
    },
    detener() {
      if (this.interval) clearInterval(this.interval);
      this.detenerRotacionCategorias();
      this.detenerCountdown();
      this.interval = null;
    },
  },
};
</script>

<style scoped>
.maquina-estado-v1-root{--clr-surface:#eef3f7;--clr-border:rgba(15,23,42,.08);--clr-text-primary:#0f172a;--clr-text-secondary:#475569;--font-primary:"Space Grotesk","Roboto",sans-serif;background:radial-gradient(circle at top left,rgba(15,95,151,.08),transparent 24%),linear-gradient(180deg,#f7fafc 0%,#edf2f7 100%);min-height:100%}
.toolbar-row{display:flex;align-items:center;justify-content:space-between;padding:16px 20px 12px;gap:12px}
.toolbar-left{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.toolbar-title{display:flex;align-items:center;gap:10px;font-family:var(--font-primary);font-size:19px;font-weight:800;color:var(--clr-text-primary);padding:6px 14px;border-radius:14px;background:linear-gradient(180deg,rgba(255,255,255,.96),rgba(241,247,252,.94));box-shadow:0 10px 26px rgba(15,23,42,.08)}
.machine-count-pill,.page-pill{display:inline-flex;align-items:center;padding:4px 11px;border-radius:999px;background:rgba(255,255,255,.9);border:1px solid var(--clr-border);font-family:var(--font-primary);font-size:11px;font-weight:700;color:var(--clr-text-secondary)}
.page-pill{color:#0f5f97}
.fs-btn{display:inline-flex;align-items:center;padding:8px 14px;border-radius:10px;border:1px solid rgba(15,95,151,.18);background:#fff;font-family:var(--font-primary);font-size:12px;font-weight:700;color:#0f5f97;cursor:pointer;box-shadow:0 8px 22px rgba(15,23,42,.08)}
.dashboard-card.v-card{background:transparent!important;border:none!important;box-shadow:none!important}
.dashboard-body.v-card__text{padding:0 10px 24px!important}
.area-select.v-input{background:rgba(255,255,255,.95);border-radius:12px}
.filter-chips{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.filter-chip{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;border:1px solid var(--clr-border);background:#fff;font-family:var(--font-primary);font-size:12px;font-weight:600;color:var(--clr-text-secondary);cursor:pointer}
.filter-chip.active .chip-count{background:currentColor;color:#fff}
.chip-dot{width:8px;height:8px;border-radius:50%}
.chip-count{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:18px;padding:0 5px;border-radius:999px;background:var(--clr-surface);font-size:10px;font-weight:700}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;gap:14px;color:var(--clr-text-secondary);font-family:var(--font-primary)}
.loading-text,.empty-state p{margin:0}
.loading-bottom{margin-top:16px}
.pending-card{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;min-height:100%;padding:18px 12px;border-radius:16px;border:1px dashed rgba(15,95,151,.35);background:linear-gradient(180deg,rgba(255,255,255,.9),rgba(238,243,247,.9));color:#0f5f97;font-family:var(--font-primary)}
.pending-num{font-size:26px;font-weight:800;line-height:1}
.pending-lbl{font-size:11px;font-weight:600;color:var(--clr-text-secondary);text-align:center}
.maquina-estado-v1-root:fullscreen{padding:20px;overflow-y:auto;height:100vh}
@media (max-width:600px){.toolbar-row{padding:12px 14px 8px}.toolbar-title{font-size:14px;padding:6px 10px}.fs-btn-label{display:none}.fs-btn{padding:8px 10px}.dashboard-body.v-card__text{padding:0 6px 16px!important}}
@media (min-width:1264px){.dashboard-body.v-card__text{padding:0 4px 20px!important}}
</style>
